import { useCallback, useEffect, useState } from "react";
import { Storefront, ArrowsClockwise, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export const Vending = () => {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/cafeteria/vending").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  useEffect(() => { load(); }, [load]);

  const refill = async (machineId) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/cafeteria/vending/machines/${machineId}/refill`, { lines: [] });
      toast.success(`${d.machine_code} loaded — ${d.loaded.length} slot(s)`);
      load();
      if (pick) pullPicklist();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const take = async (machineId, slot) => {
    try {
      const { data: d } = await api.post(`/cafeteria/vending/machines/${machineId}/sale`, { slot, quantity: 1 });
      toast.success(`${d.item_name} — ${d.currency} ${d.amount}${d.sold_out ? " · that slot is now empty" : ""}`);
      load();
    } catch (e) { oops(e); }
  };

  const pullPicklist = async () => {
    try {
      const { data: d } = await api.get("/cafeteria/vending/picklist");
      setPick(d);
    } catch (e) { oops(e); }
  };

  if (!data) return <p className="py-8 text-center text-sm text-slate-500">Loading the machines…</p>;

  return (
    <div className="space-y-4" data-testid="vending-board">
      <div className="flex flex-wrap items-center gap-2">
        {data.can_manage && (
          <Button variant="outline" data-testid="vending-picklist-btn" onClick={pullPicklist}>
            <ClipboardText size={15} className="mr-1.5" /> Today's pick-list
          </Button>
        )}
        <span className="text-[11px] uppercase tracking-wide text-slate-500">
          {data.sales_count} sale(s) recorded
        </span>
      </div>

      {pick && (
        <div className="border border-border bg-white p-4" data-testid="vending-picklist">
          <p className="text-sm font-semibold text-[#0F172A]">Load onto the trolley</p>
          {pick.totals.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">Every machine is full — nothing to load.</p>
          ) : (
            <>
              <ul className="mt-2 flex flex-wrap gap-2">
                {pick.totals.map((t) => (
                  <li key={t.item_name} className="border border-border bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                    {t.item_name} × {t.units}
                  </li>
                ))}
              </ul>
              <ul className="mt-3 space-y-1 text-xs text-slate-600">
                {pick.lines.map((l) => (
                  <li key={l.id}>{l.machine_code} · {l.place} · slot {l.slot} — load {l.to_load} {l.item_name}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {data.machines.length === 0 ? (
        <EmptyState testid="vending-empty" title="No machines set up"
          description="An admin can add a machine and set what sits in each slot." />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="vending-machines">
          {data.machines.map((m, i) => (
            <div key={m.id} data-testid={`vending-machine-${i}`} className="border border-border bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="inline-flex items-center gap-1.5 font-semibold text-[#0F172A]">
                    <Storefront size={15} /> {m.code} · {m.place}
                  </p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                    {m.operator ? `${m.operator} · ` : ""}{m.cashless ? "cashless" : "cash"} ·{" "}
                    {m.sold_out} empty · {m.low} low
                  </p>
                </div>
                {data.can_manage && (
                  <button data-testid={`vending-refill-${i}`} onClick={() => refill(m.id)} disabled={busy}
                    className="tap-safe inline-flex items-center gap-1.5 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F7A3D] hover:text-[#0B5C2E] disabled:opacity-60">
                    <ArrowsClockwise size={13} /> Fill to par
                  </button>
                )}
              </div>
              <div className="mt-3 space-y-1.5" data-testid={`vending-slots-${i}`}>
                {m.slots.map((s, n) => (
                  <div key={s.id} data-testid={`vending-slot-${i}-${n}`}
                    className={`flex flex-wrap items-center justify-between gap-2 border px-2.5 py-1.5 text-sm ${
                      s.sold_out ? "border-border bg-slate-50 text-slate-400"
                        : s.low ? "border-[#B3241B] bg-[#FFF6E5] text-[#8E1C15]"
                          : "border-border bg-white text-slate-700"}`}>
                    <span className="font-semibold">{s.slot} · {s.item_name}</span>
                    <span className="flex items-center gap-2 text-xs">
                      {s.qty} / {s.par}{s.sold_out ? " · sold out" : s.low ? " · low" : ""}
                      <button data-testid={`vending-take-${i}-${n}`} onClick={() => take(m.id, s.slot)}
                        disabled={s.sold_out}
                        className="tap-safe border border-border px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40">
                        Take one
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {data.takings.length > 0 && (
        <div className="border border-border bg-white p-4" data-testid="vending-takings">
          <p className="text-sm font-semibold text-[#0F172A]">Takings by machine</p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {data.takings.map((t) => (
              <li key={t.machine_code} className="border border-border bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                {t.machine_code} · {t.amount.toFixed(2)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.refills.length > 0 && (
        <div className="border border-border bg-white p-4" data-testid="vending-refills">
          <p className="text-sm font-semibold text-[#0F172A]">Recent refills</p>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">
            {data.refills.map((r) => (
              <li key={r.id}>
                {r.machine_code} · {r.by} · {(r.loaded || []).length} slot(s) · {(r.created_at || "").slice(0, 16).replace("T", " ")}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
